/**
 * 菜单管理页面 — 树形表格展示菜单层级
 * 使用 getMenuTree API（后端已实现），字段与 fnd_menus 对齐
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Form,
  Tag, Switch, Popconfirm, message, Tooltip, Modal, TreeSelect, Tabs,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  DownOutlined, RightOutlined, ArrowUpOutlined, ArrowDownOutlined,
  DragOutlined, ShopOutlined, SettingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getMenuTree, deleteMenu, moveMenu, updateMenu,
} from '../../api/menu';
import type { MenuItem } from '../../api/types';
import { MenuType } from '../../api/types';
import { getIcon } from '@/utils/iconRegistry';
import MenuForm from './MenuForm';
import MenuPreview from './MenuPreview';
import Permission from '@/modules/shared/components/common/Permission';
import {
  PERM_MENUS_CREATE, PERM_MENUS_UPDATE, PERM_MENUS_DELETE,
} from '@/constants/permissions';

/** 菜单类型标签配置 — 对齐后端 1=目录, 2=菜单, 3=按钮 */
const menuTypeConfig: Record<number, { label: string; color: string }> = {
  [MenuType.DIRECTORY]: { label: '目录', color: 'purple' },
  [MenuType.MENU]: { label: '菜单', color: 'blue' },
  [MenuType.BUTTON]: { label: '按钮', color: 'green' },
};

/** 根据图标名称渲染图标 */
const renderMenuIcon = (iconName?: string) => {
  if (!iconName) return null;
  const IconComp = getIcon(iconName);
  if (IconComp) return <IconComp />;
  return <span style={{ fontSize: 12, color: '#999' }}>{iconName}</span>;
};

/** 收集树节点所有 key，用于全部展开/折叠 */
const collectAllKeys = (data: MenuItem[]): number[] => {
  const keys: number[] = [];
  const walk = (nodes: MenuItem[]) => {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        keys.push(node.id);
        walk(node.children);
      }
    }
  };
  walk(data);
  return keys;
};

const MenuManagement: React.FC = () => {
  const [menuList, setMenuList] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [workMode, setWorkMode] = useState<string>('business');

  // 表单 Drawer 状态
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentMenu, setCurrentMenu] = useState<MenuItem | null>(null);
  const [parentId, setParentId] = useState<number | undefined>(undefined);

  // 移动菜单
  const [moveVisible, setMoveVisible] = useState(false);
  const [moveMenuId, setMoveMenuId] = useState<number>(0);
  const [moveMenuName, setMoveMenuName] = useState('');
  const [moveTargetParentId, setMoveTargetParentId] = useState<number | undefined>(undefined);

  const [searchForm] = Form.useForm();

  /** 获取菜单树 */
  const fetchMenuTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMenuTree({ work_mode: workMode });
      if (res?.data) {
        const list = Array.isArray(res.data) ? res.data : (res.data as any).data || [];
        setMenuList(list);
      }
    } catch (error) {
      console.error('获取菜单树失败', error);
    } finally {
      setLoading(false);
    }
  }, [workMode]);

  useEffect(() => {
    fetchMenuTree();
  }, [fetchMenuTree]);

  /** 切换工作模式 */
  const handleModeChange = (mode: string) => {
    setWorkMode(mode);
    setExpandedRowKeys([]);
    setAllExpanded(false);
  };

  /** 搜索（前端过滤） */
  const handleSearch = () => {
    fetchMenuTree();
  };

  /** 重置 */
  const handleReset = () => {
    searchForm.resetFields();
    fetchMenuTree();
  };

  /** 展开/折叠全部 */
  const handleToggleExpand = () => {
    if (allExpanded) {
      setExpandedRowKeys([]);
      setAllExpanded(false);
    } else {
      const allKeys = collectAllKeys(menuList);
      setExpandedRowKeys(allKeys);
      setAllExpanded(true);
    }
  };

  /** 新增菜单（顶级） */
  const handleAdd = () => {
    setCurrentMenu(null);
    setParentId(undefined);
    setDrawerVisible(true);
  };

  /** 编辑菜单 */
  const handleEdit = (record: MenuItem) => {
    setCurrentMenu(record);
    setParentId(record.parent_id ?? undefined);
    setDrawerVisible(true);
  };

  /** 新增子菜单 */
  const handleAddChild = (record: MenuItem) => {
    setCurrentMenu(null);
    setParentId(record.id);
    setDrawerVisible(true);
  };

  /** 删除菜单 */
  const handleDelete = async (id: number) => {
    try {
      await deleteMenu(id);
      message.success('删除成功');
      fetchMenuTree();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  /** 切换状态（使用 updateMenu API） */
  const handleToggleStatus = async (record: MenuItem, checked: boolean) => {
    try {
      await updateMenu(record.id, { status: checked ? 1 : 0 } as any);
      message.success(checked ? '已启用' : '已禁用');
      fetchMenuTree();
    } catch (error) {
      console.error('切换状态失败', error);
    }
  };

  /** 表单成功回调 */
  const handleFormSuccess = () => {
    setDrawerVisible(false);
    setCurrentMenu(null);
    setParentId(undefined);
    fetchMenuTree();
  };

  /** 关闭 Drawer */
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setCurrentMenu(null);
    setParentId(undefined);
  };

  /** 在同级菜单中找到当前节点的兄弟列表 */
  const findSiblings = (data: MenuItem[], targetId: number): MenuItem[] | null => {
    for (const item of data) {
      if (item.id === targetId) return data;
      if (item.children?.length) {
        const found = findSiblings(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  /** 重新编号兄弟节点的 sort_order，保证排序正确 */
  const renumberSiblings = async (orderedSiblings: MenuItem[]) => {
    await Promise.all(
      orderedSiblings.map((item, i) =>
        moveMenu(item.id, item.parent_id ?? 0, (i + 1) * 10)
      )
    );
  };

  /** 上移 */
  const handleMoveUp = async (record: MenuItem) => {
    const siblings = findSiblings(menuList, record.id);
    if (!siblings) return;
    const idx = siblings.findIndex((s) => s.id === record.id);
    if (idx <= 0) { message.info('已在最前'); return; }
    const newOrder = [...siblings];
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    try {
      await renumberSiblings(newOrder);
      message.success('排序已更新');
      fetchMenuTree();
    } catch {
      message.error('排序失败，请刷新后重试');
      fetchMenuTree();
    }
  };

  /** 下移 */
  const handleMoveDown = async (record: MenuItem) => {
    const siblings = findSiblings(menuList, record.id);
    if (!siblings) return;
    const idx = siblings.findIndex((s) => s.id === record.id);
    if (idx >= siblings.length - 1) { message.info('已在最后'); return; }
    const newOrder = [...siblings];
    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
    try {
      await renumberSiblings(newOrder);
      message.success('排序已更新');
      fetchMenuTree();
    } catch {
      message.error('排序失败，请刷新后重试');
      fetchMenuTree();
    }
  };

  /** 打开移动弹窗 */
  const handleOpenMove = (record: MenuItem) => {
    setMoveMenuId(record.id);
    setMoveMenuName(record.title);
    setMoveTargetParentId(undefined);
    setMoveVisible(true);
  };

  /** 确认移动 */
  const handleConfirmMove = async () => {
    try {
      // 清空 TreeSelect 时 moveTargetParentId 为 undefined，需发送 0 表示移到顶级
      await moveMenu(moveMenuId, moveTargetParentId ?? 0);
      message.success('移动成功');
      setMoveVisible(false);
      fetchMenuTree();
    } catch {
      message.error('移动失败');
    }
  };

  /** 将菜单树转换为 TreeSelect 格式 */
  const convertToTreeSelectData = (items: MenuItem[]): any[] => {
    return items
      .filter(item => item.menu_type !== MenuType.BUTTON)
      .map(item => ({
        title: item.title,
        value: item.id,
        key: item.id,
        children: item.children ? convertToTreeSelectData(item.children) : [],
      }));
  };

  const columns: ColumnsType<MenuItem> = [
    {
      title: '菜单标题',
      dataIndex: 'title',
      width: 240,
      render: (title: string, record: MenuItem) => (
        <Space>
          {renderMenuIcon(record.icon)}
          <span>{title}</span>
        </Space>
      ),
    },
    {
      title: '路由名称',
      dataIndex: 'name',
      width: 160,
    },
    {
      title: '菜单类型',
      dataIndex: 'menu_type',
      width: 100,
      align: 'center',
      render: (type: number) => {
        const config = menuTypeConfig[type];
        if (!config) return <Tag>未知</Tag>;
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '路由路径',
      dataIndex: 'path',
      width: 200,
      ellipsis: true,
      render: (path: string) => path || '-',
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      width: 80,
      align: 'center',
      render: (sort: number) => sort ?? 0,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      align: 'center',
      render: (status: number, record: MenuItem) => (
        <Switch
          checked={status === 1}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          size="small"
          onChange={(checked) => handleToggleStatus(record, checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      render: (_: unknown, record: MenuItem) => (
        <Space size={0} wrap>
          <Permission permission={PERM_MENUS_UPDATE}>
            <Button type="link" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>编辑</Button>
          </Permission>
          <Permission permission={PERM_MENUS_CREATE}>
            {record.menu_type !== MenuType.BUTTON && (
              <Button type="link" icon={<PlusOutlined />} size="small" onClick={() => handleAddChild(record)}>子级</Button>
            )}
          </Permission>
          <Permission permission={PERM_MENUS_UPDATE}>
            <Tooltip title="上移">
              <Button type="text" icon={<ArrowUpOutlined />} size="small" onClick={() => handleMoveUp(record)} />
            </Tooltip>
          </Permission>
          <Permission permission={PERM_MENUS_UPDATE}>
            <Tooltip title="下移">
              <Button type="text" icon={<ArrowDownOutlined />} size="small" onClick={() => handleMoveDown(record)} />
            </Tooltip>
          </Permission>
          <Permission permission={PERM_MENUS_UPDATE}>
            <Tooltip title="移动到">
              <Button type="text" icon={<DragOutlined />} size="small" onClick={() => handleOpenMove(record)} />
            </Tooltip>
          </Permission>
          <Permission permission={PERM_MENUS_DELETE}>
            <Popconfirm
              title="确定要删除该菜单吗？"
              description="删除后不可恢复，子菜单将同时被删除"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">删除</Button>
            </Popconfirm>
          </Permission>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 16 }}>
      <Card
        title="菜单管理"
        bordered={false}
        style={{ flex: 1, minWidth: 0 }}
        extra={
          <Tabs
            activeKey={workMode}
            onChange={handleModeChange}
            size="small"
            items={[
              {
                key: 'business',
                label: (
                  <span><ShopOutlined style={{ marginRight: 4 }} />业务模式</span>
                ),
              },
              {
                key: 'admin',
                label: (
                  <span><SettingOutlined style={{ marginRight: 4 }} />管理模式</span>
                ),
              },
            ]}
            style={{ marginBottom: -16 }}
          />
        }
      >
        {/* 搜索栏 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="title">
            <Input placeholder="菜单标题关键词" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="type">
            <Select
              placeholder="菜单类型"
              style={{ width: 130 }}
              allowClear
            >
              <Select.Option value={MenuType.DIRECTORY}>目录</Select.Option>
              <Select.Option value={MenuType.MENU}>菜单</Select.Option>
              <Select.Option value={MenuType.BUTTON}>按钮</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Permission permission={PERM_MENUS_CREATE}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增菜单
              </Button>
            </Permission>
            <Button
              icon={allExpanded ? <DownOutlined /> : <RightOutlined />}
              onClick={handleToggleExpand}
            >
              {allExpanded ? '折叠全部' : '展开全部'}
            </Button>
          </Space>
        </div>

        {/* 树形表格 */}
        <Table<MenuItem>
          rowKey="id"
          columns={columns}
          dataSource={menuList}
          loading={loading}
          pagination={false}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => {
              setExpandedRowKeys(keys as React.Key[]);
              const allKeys = collectAllKeys(menuList);
              setAllExpanded((keys as React.Key[]).length === allKeys.length && allKeys.length > 0);
            },
            childrenColumnName: 'children',
          }}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      {/* 实时预览侧边栏 */}
      <MenuPreview menuList={menuList} workMode={workMode} />

      {/* 菜单编辑 Drawer */}
      <MenuForm
        visible={drawerVisible}
        menu={currentMenu}
        parentId={parentId}
        workMode={workMode}
        onClose={handleDrawerClose}
        onSuccess={handleFormSuccess}
      />

      {/* 移动菜单弹窗 */}
      <Modal
        title={`移动菜单「${moveMenuName}」`}
        open={moveVisible}
        onOk={handleConfirmMove}
        onCancel={() => setMoveVisible(false)}
        width={500}
      >
        <div style={{ marginBottom: 8, color: '#666' }}>选择目标上级菜单（留空移动到顶级）：</div>
        <TreeSelect
          style={{ width: '100%' }}
          placeholder="选择目标上级菜单"
          value={moveTargetParentId}
          onChange={setMoveTargetParentId}
          treeData={convertToTreeSelectData(menuList)}
          allowClear
          showSearch
          treeNodeFilterProp="title"
          treeDefaultExpandAll
        />
      </Modal>
    </div>
  );
};

export default MenuManagement;
